import { PortableTextProjection } from '@lib/sanity/types/common';
import { PortableText as ReactPortableText } from '@portabletext/react';
import Anchor from '@ui/Anchor/Anchor';
import cn from '@utils/cn';
import TextBlock from './TextBlock';
import { SharedTextProps, TextAs, TextVariant, VARIANTS } from './constants';

export { TextVariant } from './constants';

const DOMINANT_ELEMENTS = ['h1', 'h2'];

type PortableTextProps = SharedTextProps & {
  content?: PortableTextProjection;
};

export default function PortableText({ content, variant, as, className, style }: PortableTextProps) {
  if (!content?.value) {
    return null;
  }

  const render = VARIANTS[variant];
  const isHeading = DOMINANT_ELEMENTS.includes(render.element);
  const blockAs = getAs(variant, as);

  return (
    <TextBlock variant={variant} as={blockAs} className={cn('portable-text', className)} style={style}>
      <ReactPortableText
        value={content.value}
        components={{
          block: {
            normal: ({ children }) => {
              // When inside a heading element (h1, h2), render as span instead of p
              // to avoid invalid HTML like <h1><p>text</p></h1>
              if (isHeading) {
                return <span>{children}</span>;
              }
              return <p>{children}</p>;
            },
          },
          marks: {
            color: ({ children, value }) => {
              return (
                <span
                  style={{
                    color: value.rgb ? `rgba(${value.rgb.r},${value.rgb.g},${value.rgb.b},${value.rgb.a})` : undefined,
                  }}
                >
                  {children}
                </span>
              );
            },
            link: ({ children, value }) => {
              return (
                <Anchor href={value?.url} isInline>
                  {children}
                </Anchor>
              );
            },
          },
          list: {
            bullet: ({ children }) => <ul className="list-disc pl-8">{children}</ul>,
            number: ({ children }) => <ol className="list-decimal pl-8">{children}</ol>,
          },
        }}
      />
    </TextBlock>
  );
}

function getAs(variant: TextVariant, as?: TextAs) {
  if (as) {
    return as;
  }

  const render = VARIANTS[variant];

  if (DOMINANT_ELEMENTS.includes(render.element)) {
    return render.element;
  }

  return 'div';
}
